import { useState, useEffect } from 'react'
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth'
import { candidateAuth, SMARTHIRE_API, CANDIDATE_DASHBOARD } from './SmartHireAuth'
import './SmartHireSignInModal.css'

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  )
}

export default function SmartHireCandidateSignInModal({ onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Lock body scroll while modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  const signIn = async (provider: any, providerKey: string) => {
    setLoading(providerKey)
    setError('')
    try {
      await setPersistence(candidateAuth, browserLocalPersistence)
      const result = await signInWithPopup(candidateAuth, provider)
      const idToken = await result.user.getIdToken()

      const res = await fetch(`${SMARTHIRE_API}/session_login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ idToken, userType: 'aspUser' })
      })
      const data = await res.json()

      if (data.status === 'refresh_token') {
        const freshToken = await result.user.getIdToken(true)
        const res2 = await fetch(`${SMARTHIRE_API}/session_login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ idToken: freshToken, userType: 'aspUser' })
        })
        const data2 = await res2.json()
        if (data2.status === 'failed') {
          setError(data2.reason === 'user_not_found'
            ? 'Account not found. Please ask your recruiter to invite you.'
            : 'Sign-in failed. Please try again.')
        } else {
          window.location.href = CANDIDATE_DASHBOARD
        }
      } else if (data.status === 'failed') {
        setError(data.reason === 'user_not_found'
          ? 'Account not found. Please ask your recruiter to invite you.'
          : 'Sign-in failed. Please try again.')
      } else {
        window.location.href = CANDIDATE_DASHBOARD
      }
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError('Sign-in failed. Please try again.')
      }
    }
    setLoading(null)
  }

  const isLoading = !!loading

  return (
    <div className="sh-modal-backdrop" onClick={handleBackdropClick}>
      <div className="sh-modal" role="dialog" aria-modal="true" aria-labelledby="sh-cand-title">

        {/* Header */}
        <div className="sh-modal__header">
          <div className="sh-modal__title-wrap">
            <div>
              <h2 className="sh-modal__title" id="sh-cand-title">SmartDevelop Sign In</h2>
              <p className="sh-modal__subtitle">Prepare for your career</p>
            </div>
          </div>
          <button className="sh-modal__close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1.5 1.5l13 13M14.5 1.5l-13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sh-modal__body">
          <p className="sh-modal__helper-text">
            Sign in with your Google or Microsoft account.
          </p>

          {error && <div className="sh-modal__error">{error}</div>}

          <button
            className="sh-modal__oauth-btn"
            onClick={() => signIn(new GoogleAuthProvider(), 'google')}
            disabled={isLoading}
          >
            {loading === 'google' ? <span className="sh-spinner" /> : <GoogleIcon />}
            <span>{loading === 'google' ? 'Signing in…' : 'Continue with Google'}</span>
          </button>

          <button
            className="sh-modal__oauth-btn"
            onClick={() => signIn(new OAuthProvider('microsoft.com'), 'microsoft')}
            disabled={isLoading}
          >
            {loading === 'microsoft' ? <span className="sh-spinner" /> : <MicrosoftIcon />}
            <span>{loading === 'microsoft' ? 'Signing in…' : 'Continue with Microsoft'}</span>
          </button>
        </div>

      </div>
    </div>
  )
}
